import Collection from 'ol/Collection';
import { primaryAction } from 'ol/events/condition';

import { useAnnotationSessionStore } from '@stores/useAnnotationSessionStore';

import { setTranslateInteraction } from './translate.interaction';
import { setModifyInteraction } from './modify.interaction';
import { pushHistory, clearHistory } from './boxHistory';
import {
	selectedBoxId,
	selectBox,
	setAnnotationScoped,
	setHoveredBox,
	subscribeBoxSelection,
} from './annotation.interaction';

/**
 * The select-first editing of the object sessions (Collection view): a box is
 * edited once it is selected, and a press on a box selects it, so one drag moves
 * it in either mode. The session owns the selection, Modify and
 * Translate (bound to a collection holding just the selected box), click and
 * hover handling, the Draw / Select mode (Select only keeps an empty-map drag from
 * sketching) and the undo entries of the edits. The
 * caller owns the Draw interaction and hands it over with `attachDraw`; both the
 * orthophoto (`object.draw.js`) and the single image (`singleImage.draw.js`)
 * build on it.
 *
 * One scoped session runs at a time. It registers itself here so the keys and
 * the toolbar reach it without knowing which flow created it.
 */

export const OBJECT_MODE = Object.freeze({ DRAW: 'draw', SELECT: 'select' });

const HIT_TOLERANCE = 4;
// A click that finishes a box must not also select it (singleclick fires after the up).
const SELECT_AFTER_DRAW_MS = 500;

let current = null;
const modeListeners = new Set();

/** The scoped session that is running, or null (legacy and Model view sessions never register one). */
export const getActiveSelectFirst = () => current;

/** Hear about mode changes (the toolbar follows a click-select); returns the unsubscribe. */
export const subscribeSelectFirstMode = listener => {
	modeListeners.add(listener);
	return () => modeListeners.delete(listener);
};

const sameGeometry = (a, b) => {
	const first = a?.getFlatCoordinates?.();
	const second = b?.getFlatCoordinates?.();
	return (
		Boolean(first && second) &&
		first.length === second.length &&
		first.every((value, index) => value === second[index])
	);
};

/** Push an undo entry when a move or reshape really changed the box. */
const trackGeometryHistory = (interaction, startEvent, endEvent) => {
	let before = null;
	interaction.on(startEvent, event => {
		const feature = event.features.item(0);
		before = feature && {
			feature,
			geometry: feature.getGeometry().clone(),
			saved: feature.get('saved'),
			type: feature.get('annotation_type'),
		};
	});
	interaction.on(endEvent, () => {
		if (!before) return;
		const { feature, geometry, saved, type } = before;
		before = null;
		if (sameGeometry(feature.getGeometry(), geometry)) return;
		pushHistory({
			undo: () => {
				feature.setGeometry(geometry);
				feature.set('annotation_type', type, true);
				feature.set('saved', saved);
				feature.changed();
			},
		});
	});
};

/**
 * @param {object} options
 * @param {import('ol/Map').default} options.map
 * @param {import('ol/source/Vector').default} options.source - the boxes of the session
 * @param {import('ol/layer/Vector').default} options.layer - the layer of `source`; only its boxes are hit
 * @param {(feature) => boolean} options.isSelectable - whether a box may be selected, moved or reshaped
 * @param {(feature) => void} [options.updateModified] - a box was moved or reshaped
 * @param {(feature) => (() => void)|void} [options.forgetBox] - take a box out of the caller's own
 *   lists; may return the function that puts it back (used by the undo of a removal)
 * @param {boolean} [options.rotateEnabled] - Modify rotates from the outer vertices
 * @param {boolean} [options.active] - drawing and editing are on (off while no tile is open)
 * @param {import('ol/interaction/Snap').default} [options.snap] - switched on and off with the session
 */
export const createSelectFirstSession = ({
	map,
	source,
	layer,
	isSelectable,
	updateModified = () => {},
	forgetBox,
	rotateEnabled = false,
	active = true,
	snap = null,
}) => {
	let mode = OBJECT_MODE.DRAW;
	let rotate = Boolean(rotateEnabled);
	let drawingActive = active;
	let sketching = false;
	let lastDrawEnd = 0;
	let draw = null;
	let detachDraw = () => {};
	let stopped = false;

	// The one feature Modify and Translate may touch: the selected box.
	const selection = new Collection();

	// A press on a box starts Translate even when the pointer does not move, and
	// its end would mark the box changed (a model box would turn into an edit). The
	// shape at the start of each move / reshape decides whether anything changed.
	const shapeAtStart = new WeakMap();
	const rememberShape = event => {
		const feature = event.features.item(0);
		if (feature) shapeAtStart.set(feature, feature.getGeometry().clone());
	};
	const markIfChanged = feature => {
		const before = shapeAtStart.get(feature);
		shapeAtStart.delete(feature);
		if (before && sameGeometry(feature.getGeometry(), before)) return;
		updateModified(feature);
	};

	const hitSelectable = (targetMap, pixel) =>
		targetMap.forEachFeatureAtPixel(
			pixel,
			feature => (isSelectable(feature) ? feature : undefined),
			{
				layerFilter: candidate => candidate === layer,
				hitTolerance: HIT_TOLERANCE,
			}
		);

	const boxById = boxId =>
		boxId ? source.getFeatures().find(feature => feature.get('boxId') === boxId) : undefined;

	const translate = setTranslateInteraction({
		layer,
		features: selection,
		updateModifiedObjects: markIfChanged,
	});
	translate.on('translatestart', rememberShape);
	trackGeometryHistory(translate, 'translatestart', 'translateend');

	const createModify = () => {
		const interaction = setModifyInteraction({
			features: selection,
			updateModifiedObjects: markIfChanged,
			isRotateEnabled: rotate,
		});
		interaction.on('modifystart', rememberShape);
		trackGeometryHistory(interaction, 'modifystart', 'modifyend');
		return interaction;
	};
	let modify = createModify();

	setAnnotationScoped(true);
	map.addInteraction(translate);
	map.addInteraction(modify);

	/** Which interactions are on, from the open tile and the mode. */
	const apply = () => {
		// Moving and reshaping work in both modes, on the selected box only; the mode
		// decides whether an empty-map drag sketches a box (Draw) or is left alone.
		draw?.setActive(drawingActive && mode === OBJECT_MODE.DRAW);
		snap?.setActive(drawingActive);
		modify.setActive(drawingActive);
		translate.setActive(drawingActive);
	};

	const setMode = next => {
		if (next === mode) return;
		if (next !== OBJECT_MODE.DRAW && next !== OBJECT_MODE.SELECT) return;
		mode = next;
		apply();
		modeListeners.forEach(listener => listener(next));
	};

	// Selection sync, click-to-select, hover and the cursor.
	const stopSelection = subscribeBoxSelection(boxId => {
		selection.clear();
		const box = boxById(boxId);
		if (box) selection.push(box);
	});

	// A box taken off the map (a switch, a reload of the object) cannot stay selected.
	const onRemoved = event => {
		if (selectedBoxId && event.feature.get('boxId') === selectedBoxId) selectBox(null);
	};
	source.on('removefeature', onRemoved);

	const onSingleClick = event => {
		if (!drawingActive || sketching) return;
		if (Date.now() - lastDrawEnd < SELECT_AFTER_DRAW_MS) return;
		const hit = hitSelectable(event.map, event.pixel);
		if (!hit) {
			selectBox(null);
			return;
		}
		selectBox(hit.get('boxId') ?? null);
	};
	map.on('singleclick', onSingleClick);

	// A press on a box picks it before Translate / Modify look at the press, so a
	// box moves with one drag, without a click first (and without leaving Draw).
	const onPointerDown = event => {
		if (!drawingActive || sketching || event.originalEvent?.button > 0) return;
		const hit = hitSelectable(event.map, event.pixel);
		if (hit && hit.get('boxId') && hit.get('boxId') !== selectedBoxId) {
			selectBox(hit.get('boxId'));
		}
	};
	map.on('pointerdown', onPointerDown);

	const onPointerMove = event => {
		if (event.dragging) return;
		const hit = drawingActive ? hitSelectable(event.map, event.pixel) : undefined;
		setHoveredBox(hit?.get('boxId') ?? null);
		const target = event.map.getTargetElement?.();
		// The selected box shows Translate's own grab cursor.
		const clickable = hit && hit.get('boxId') !== selectedBoxId;
		if (target) target.style.cursor = clickable ? 'pointer' : '';
	};
	map.on('pointermove', onPointerMove);

	/**
	 * The Draw `condition`: a press on a selectable box belongs to selecting it,
	 * never to a new sketch (a click that finishes a sketch may land on one, so it
	 * always goes through).
	 */
	const drawCondition = event =>
		primaryAction(event) && (sketching || !hitSelectable(event.map, event.pixel));

	/** Hand over the Draw interaction (again, after it was rebuilt); the previous one is let go. */
	const attachDraw = nextDraw => {
		detachDraw();
		draw = nextDraw;
		if (!draw) return;

		const onStart = () => {
			sketching = true;
		};
		const onAbort = () => {
			sketching = false;
		};
		const onEnd = event => {
			sketching = false;
			lastDrawEnd = Date.now();
			const added = event.feature;
			pushHistory({
				undo: () => {
					forgetBox?.(added);
					if (source.hasFeature(added)) source.removeFeature(added);
				},
			});
		};
		draw.on('drawstart', onStart);
		draw.on('drawabort', onAbort);
		draw.on('drawend', onEnd);
		const attached = draw;
		detachDraw = () => {
			attached.un('drawstart', onStart);
			attached.un('drawabort', onAbort);
			attached.un('drawend', onEnd);
			sketching = false;
			draw = null;
			detachDraw = () => {};
		};
		apply();
	};

	/** Abandon the box being sketched, if any. */
	const cancelSketch = () => {
		if (!sketching) return false;
		draw?.abortDrawing();
		sketching = false;
		return true;
	};

	/**
	 * Esc: first the sketch, then the selection.
	 * @returns {'sketch'|'selection'|null} what it dismissed; null means nothing was left to dismiss
	 */
	const handleEscape = () => {
		if (cancelSketch()) return 'sketch';
		if (selectedBoxId) {
			selectBox(null);
			return 'selection';
		}
		return null;
	};

	/**
	 * Remove the selected box, and remember how to bring it back. A removed model
	 * box is recorded as a reject by the session's own `removefeature` listener;
	 * the undo drops that reject again.
	 * @returns {{ feature, entry: { undo: () => void } }|null} null when nothing is selected
	 */
	const removeSelectedBox = () => {
		if (!selectedBoxId) return null;
		const feature = boxById(selectedBoxId);
		if (!feature) {
			selectBox(null);
			return null;
		}
		const boxId = feature.get('boxId');
		const restoreBox = forgetBox?.(feature);
		source.removeFeature(feature);
		selectBox(null);

		const entry = {
			undo: () => {
				// Before the box is back, so the session never sees it as both present and
				// rejected. Only a model box or a correction records a reject when erased;
				// for any other box this finds none and does nothing.
				useAnnotationSessionStore
					.getState()
					.clearRejectFor(feature.get('nodeUuid'), feature.get('tileName'), boxId);
				restoreBox?.();
				source.addFeature(feature);
				selectBox(boxId);
			},
		};
		pushHistory(entry);
		return { feature, entry };
	};

	/** Turn rotation on or off while reshaping: Modify is rebuilt with it. */
	const setRotateEnabled = enabled => {
		const next = Boolean(enabled);
		if (next === rotate) return;
		rotate = next;
		if (stopped) return;
		map.removeInteraction(modify);
		modify = createModify();
		map.addInteraction(modify);
		apply();
	};

	/** Turn drawing and editing on or off, keeping the interactions on the map. */
	const setActive = next => {
		drawingActive = Boolean(next);
		apply();
	};

	const stop = () => {
		if (stopped) return;
		stopped = true;
		selectBox(null);
		stopSelection();
		source.un('removefeature', onRemoved);
		map.un('singleclick', onSingleClick);
		map.un('pointerdown', onPointerDown);
		map.un('pointermove', onPointerMove);
		detachDraw();
		map.removeInteraction(modify);
		map.removeInteraction(translate);
		setAnnotationScoped(false);
		setHoveredBox(null);
		clearHistory();
		const target = map.getTargetElement?.();
		if (target) target.style.cursor = '';
		if (current === session) current = null;
	};

	const session = {
		getMode: () => mode,
		setMode,
		subscribe: subscribeSelectFirstMode,
		handleEscape,
		removeSelectedBox,
		setRotateEnabled,
		cancelSketch,
		isSketching: () => sketching,
		setActive,
		attachDraw,
		drawCondition,
		getModify: () => modify,
		getTranslate: () => translate,
		stop,
	};

	current = session;
	apply();
	return session;
};
